import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { employeesApi } from '../../features/colaboradores/api';
import type { EmployeeListItem } from '../../features/colaboradores/types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

export interface ColaboradorSelectProps {
  value: number | null;
  onChange: (id: number | null, item: EmployeeListItem | null) => void;
  label?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** Inclui colaboradores demitidos no resultado. Default false. */
  incluirDemitidos?: boolean;
}

const PAGE_SIZE = 50;

export function ColaboradorSelect({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  disabled,
  incluirDemitidos,
}: ColaboradorSelectProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const search = useDebouncedValue(input, 250);

  const list = useQuery({
    queryKey: ['employees', 'select', { search, incluirDemitidos }],
    queryFn: () =>
      employeesApi.list({
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'nome',
        sortOrder: 'asc',
        search: search || undefined,
        includeInactive: incluirDemitidos ?? false,
      }),
    enabled: open,
    placeholderData: (previous) => previous,
  });

  const detail = useQuery({
    queryKey: ['employees', 'detail', value],
    queryFn: () => employeesApi.get(value as number),
    enabled: value !== null && value !== undefined,
  });

  const options: EmployeeListItem[] = list.data?.data ?? [];
  const selectedFromList = options.find((o) => o.id === value) ?? null;
  const selected: EmployeeListItem | null =
    selectedFromList ??
    (detail.data
      ? {
          id: detail.data.id,
          nome: detail.data.nome,
          cpf: detail.data.cpf,
          tipoContrato: detail.data.tipoContrato,
          cargoNome: detail.data.cargoNome,
          email: detail.data.email,
          telefone: detail.data.telefone,
          statusLogin: detail.data.statusLogin,
          dataAdmissao: detail.data.dataAdmissao,
          dataDemissao: detail.data.dataDemissao,
          ativo: detail.data.ativo,
          criadoEm: detail.data.criadoEm,
          criadoPorNome: detail.data.criadoPorNome,
          atualizadoEm: detail.data.atualizadoEm,
        }
      : null);

  return (
    <Autocomplete<EmployeeListItem>
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setInput(v);
      }}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.nome}
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={
        list.isError ? t('components.asyncSelect.error') : t('components.asyncSelect.noOptions')
      }
      loadingText={t('components.asyncSelect.loading')}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.colaboradorSelect.label')}
          placeholder={placeholder ?? t('components.colaboradorSelect.placeholder')}
          error={error || list.isError}
          helperText={list.isError ? t('components.asyncSelect.error') : helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
